// Quẻ Kinh Dịch — atom dùng chung (Line, HexLine, helper chuyển quẻ→hào) + bản
// TĨNH HexagramFullBoard (gốc → biến). Tách từ routes/_authenticated.i-ching.tsx
// để i-ching / tong-hop dùng chung (DRY). KHÔNG chứa state tương tác của trang.
import type { HexagramResult } from "@/lib/boide-api";

export type Line = { value: number; changing: boolean };

export const lineToBit = (v: number): "0" | "1" => (v === 7 || v === 9 ? "1" : "0");

export function queToLines(que?: HexagramResult["que_chinh"] | null): Line[] {
  const lucHao = que?.luc_hao;
  if (!lucHao?.length) return [];

  return lucHao
    .slice()
    .sort((a, b) => a.vi_tri - b.vi_tri)
    .map((hao, index) => {
      const bit = hao.bit ?? que?.binary_6?.[index];
      const isYang = bit === 1 || (bit == null && hao.loai.toLowerCase().includes("dương"));
      const changing = hao.is_moving ?? hao.bien;
      return { value: changing ? (isYang ? 9 : 6) : isYang ? 7 : 8, changing };
    });
}

export function hexagramToLines(hexagram?: HexagramResult | null): Line[] {
  return queToLines(hexagram?.que_chinh);
}

export function hexagramToBienLines(hexagram?: HexagramResult | null): Line[] {
  return queToLines(hexagram?.que_bien);
}

export function HexLine({
  line,
  animate,
  active,
}: {
  line: Line;
  animate?: boolean;
  active?: boolean;
}) {
  const isYang = lineToBit(line.value) === "1";
  const cls = `h-3 rounded-sm bg-gold-gradient ${active ? "hao-active" : "shadow-gold"}`;
  return isYang ? (
    <div className="flex h-3 w-full">
      <div className={`${cls} flex-1 ${animate ? "animate-hao-yang" : ""}`} />
    </div>
  ) : (
    <div className="flex h-3 w-full gap-2">
      <div className={`${cls} flex-1 ${animate ? "animate-hao-yin-l" : ""}`} />
      <div className={`${cls} flex-1 ${animate ? "animate-hao-yin-r" : ""}`} />
    </div>
  );
}

/** Một cột quẻ (gốc hoặc biến): nhãn + tên + tượng + số quẻ + 6 hào (dưới lên). */
function HexColumn({
  label,
  title,
  subtitle,
  soQue,
  lines,
}: {
  label: string;
  title?: string;
  subtitle?: string;
  soQue?: number | null;
  lines: Line[];
}) {
  return (
    <div className="flex flex-col items-center">
      <span className={`font-display text-[9px] md:text-[10px] tracking-[0.3em] md:tracking-[0.5em] ${label.includes("BIẾN") ? "iching-accent" : "text-primary"}`}>
        {label}
      </span>
      <h2 className="mt-1 md:mt-2 text-center font-serif-soft text-lg sm:text-xl md:text-2xl italic text-gold-gradient">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-1 text-center text-[10px] sm:text-xs italic text-muted-foreground">
          {subtitle}
        </p>
      )}
      {soQue != null && (
        <p className="mt-1 font-display text-[8px] md:text-[9px] tracking-[0.2em] md:tracking-[0.25em] text-primary/80">
          QUẺ SỐ {soQue}
        </p>
      )}

      <div className="mt-3 md:mt-6 flex w-[130px] sm:w-60 flex-col-reverse gap-1.5 sm:gap-3">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-1.5 md:gap-2">
            <span className="font-display text-[8px] sm:text-[9px] tracking-widest text-muted-foreground/70 min-w-[20px] sm:min-w-fit">
              <span className="sm:hidden">{`H${i + 1}`}</span>
              <span className="hidden sm:inline">{`HÀO ${i + 1}`}</span>
            </span>
            <div className="relative flex-1">
              <HexLine line={l} animate={false} active={l.changing} />
            </div>
            {l.changing && (
              <span className="iching-accent font-display text-[8px] sm:text-[9px] tracking-widest">
                <span className="sm:hidden">★</span>
                <span className="hidden sm:inline">BIẾN</span>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Quẻ Kinh Dịch đầy đủ — bản TĨNH (gốc → biến). Nhận thẳng HexagramResult từ API,
 * dùng ở nơi chỉ cần xem (vd chat tổng hợp). Trông giống quẻ trên trang Kinh Dịch.
 */
export function HexagramFullBoard({ hex }: { hex: HexagramResult }) {
  const main = hex.que_chinh;
  const changed = hex.que_bien;
  const lines = hexagramToLines(hex);
  const hasBien = !!hex.hao_dong && !!changed;
  const bienLines = hasBien ? hexagramToBienLines(hex) : [];

  const mainTitle = main?.ten_tuong || main?.ten || "";
  const mainSubtitle = [main?.ten, main?.cung_que, main?.ngu_hanh].filter(Boolean).join(" · ");
  const changedTitle = changed?.ten_tuong || changed?.ten || "";
  const changedSubtitle = [changed?.ten, changed?.cung_que, changed?.ngu_hanh]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <div
        className={`flex w-full items-center justify-center gap-3 md:gap-4 ${
          hasBien
            ? "flex-row md:grid md:grid-cols-[1fr_auto_1fr]"
            : "flex-col md:grid md:grid-cols-1"
        }`}
      >
        <HexColumn
          label="QUẺ GỐC"
          title={mainTitle}
          subtitle={mainSubtitle}
          soQue={main?.so_que}
          lines={lines}
        />

        {hasBien && (
          <>
            <div className="flex flex-col items-center justify-center my-1 md:my-0 min-w-8 sm:min-w-[100px]">
              <svg
                width="100"
                height="40"
                viewBox="0 0 100 40"
                className="overflow-visible w-8 sm:w-[100px] h-10"
              >
                <defs>
                  <linearGradient id="arrG-static" x1="0" x2="1">
                    <stop offset="0%" stopColor="oklch(0.55 0.08 190)" stopOpacity="0" />
                    <stop offset="35%" stopColor="oklch(0.62 0.09 190)" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="oklch(0.52 0.08 190)" />
                  </linearGradient>
                </defs>
                <path
                  d="M 2 20 Q 50 14, 92 20"
                  stroke="url(#arrG-static)"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.9"
                />
                <path d="M 84 15 L 94 20 L 84 25" stroke="oklch(0.52 0.08 190)" strokeWidth="1.5" fill="none" />
              </svg>
              <span className="iching-accent mt-1 font-display text-[8px] sm:text-[9px] tracking-[0.3em]">
                BIẾN
              </span>
            </div>

            <HexColumn
              label="QUẺ BIẾN"
              title={changedTitle}
              subtitle={changedSubtitle}
              soQue={changed?.so_que}
              lines={bienLines}
            />
          </>
        )}
      </div>

      <p className="text-center text-[10px] sm:text-xs italic text-muted-foreground">
        Quẻ Gốc cho hiện tại, Quẻ Biến chỉ tương lai khi hào động chuyển hoá.
      </p>
    </div>
  );
}
